import {DEFAULT_MAXIMUM_ASSIGNEES} from '@github-ui/item-picker-assignee'
import {testIdProps} from '@github-ui/test-id-props'
import useIsMounted from '@github-ui/use-is-mounted'
import {useSyncedState} from '@github-ui/use-synced-state'
import type {
  SelectPanelGroupedListProps as GroupedListProps,
  SelectPanelItemProps as ItemProps,
  SelectPanelProps,
} from '@primer/react'
import {useCallback, useEffect, useEffectEvent, useMemo, useRef} from 'react'

import {ItemValueAdd, ItemValueEdit} from '../../api/stats/contracts'
import {SelectMenuError} from '../../components/common/select-menu/select-menu-error'
import type {UseSummaryOpenChange} from '../../components/table/editors/use-summary-anchor'
import {getInitialState} from '../../helpers/initial-state'
import {not_typesafe_nonNullAssertion} from '../../helpers/non-null-assertion'
import type {FuzzyFilterData} from '../../helpers/suggester'
import type {SupportedSuggestionOptions} from '../../helpers/suggestions'
import type {ColumnModel} from '../../models/column-model'
import type {MemexItemModel} from '../../models/memex-item-model'
import {usePostStats} from '../common/use-post-stats'
import {useSelectMenuFilter} from '../common/use-select-menu-filter'
import {useAssigneeLimitReached} from './use-assignee-limit'
import styles from './use-select-panel.module.css'

const DEFAULT_BLANKSLATE_MESSAGE = {
  title: 'Nothing to show',
  body: '',
  variant: 'empty' as const,
}

// Maximum number of suggestions shown by this editor
const MULTI_SELECT_MAX_SUGGESTIONS = 12

export type UseSelectPanelProps<T extends SupportedSuggestionOptions> = {
  model: MemexItemModel
  selected: Array<T>
  initialFilterValue: string
  blankslateText?: string
  saveSelected: (selected: Array<T>) => Promise<void>
  onOpenChange: UseSummaryOpenChange
  /**
   * If `undefined`, the panel will always be visible when mounted and must be unmounted to
   * close and save.
   */
  open?: boolean
  fetchOptions: () => void
  options?: Array<T>
  filterOptions: (query: string, options: Array<T>, maxSuggestions: number) => FuzzyFilterData<T>
  getOptionMatchingFilterValue?: (filterValue: string, option: T) => T | null
  columnId: ColumnModel['id']
  getSortAttribute: (option: T) => string
  convertOptionToItem: (option: T, selectedItems: Array<T>) => ItemProps & T
  renderCreateNewOption?: (optionName: string) => ItemProps
  /**
   * Called with the options added by a single interaction, alongside the search
   * term in effect at the time. Deselections are not reported. Opt-in: editors
   * that do not report selection analytics simply omit it.
   */
  onOptionsAdded?: (added: Array<T>, filterValue: string) => void
  singleSelect?: boolean
  groupMetadata?: GroupedListProps['groupMetadata']
  placeholderText: string
  renderAnchor: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  maximumAssignees?: number
}

export function useSelectPanel<T extends SupportedSuggestionOptions>({
  model,
  selected: initialSelected,
  initialFilterValue,
  blankslateText,
  saveSelected,
  onOpenChange,
  open,
  fetchOptions,
  options,
  filterOptions,
  getOptionMatchingFilterValue,
  columnId,
  getSortAttribute,
  convertOptionToItem,
  renderCreateNewOption,
  onOptionsAdded,
  singleSelect = false,
  groupMetadata,
  placeholderText,
  renderAnchor,
  maximumAssignees = DEFAULT_MAXIMUM_ASSIGNEES,
}: UseSelectPanelProps<T>) {
  const {projectLimits} = getInitialState()
  const {postStats} = usePostStats()
  const [selected, setSelected] = useSyncedState(initialSelected)

  const {
    onOpen,
    setFilterValue,
    filterValue,
    suggestions: allOptions,
    filteredSuggestions: filteredOptions,
    error: fetchSuggestionsError,
  } = useSelectMenuFilter<T>({
    initialFilterValue,
    memexItemModel: model,
    columnId,
    fetchSuggestions: fetchOptions,
    filterSuggestions: filterOptions,
    maxSuggestions: singleSelect ? projectLimits.singleSelectColumnOptionsLimit : MULTI_SELECT_MAX_SUGGESTIONS,
    options,
  })

  const trackingOnOpen = useEffectEvent(onOpen)
  // Fetch options immediately on render
  useEffect(() => {
    trackingOnOpen()
  }, [model])

  const submitSelection = useCallback(
    (selectedOptions: Array<T>) => {
      saveSelected(selectedOptions)
      postStats({
        name: selected.length > 0 ? ItemValueEdit : ItemValueAdd,
        memexProjectColumnId: columnId,
        memexProjectItemId: model.itemId(),
      })
    },
    [columnId, model, postStats, saveSelected, selected.length],
  )

  const isMounted = useIsMounted()
  useEffect(() => {
    return () => {
      // Save multiselect state after unmount, regardless of how the unmount was triggered
      // Singleselect state was already saved in onSelectedChange

      if (open === undefined && !singleSelect && !isMounted()) {
        submitSelection(selected)
      }
    }
  }, [open, isMounted, selected, singleSelect, submitSelection])

  const prevOpenRef = useRef(open)
  useEffect(() => {
    // For controlled panels, save multiselect state on close instead of unmount
    if (prevOpenRef.current && !open && !singleSelect) submitSelection(selected)
    prevOpenRef.current = open
  }, [open, selected, singleSelect, submitSelection])

  const onSelectedChange = useCallback(
    (itemOrItems: Array<ItemProps> | ItemProps | undefined) => {
      // convert from single variant to multi variant because that's what is expected in submitSelection
      const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems].filter(item => item)
      const selectedOptions = (items as Array<T>).sort((a, b) => getSortAttribute(a).localeCompare(getSortAttribute(b)))

      if (onOptionsAdded) {
        const previousIds = new Set(selected.map(option => option.id))
        const added = selectedOptions.filter(option => !previousIds.has(option.id))
        if (added.length > 0) onOptionsAdded(added, filterValue)
      }

      setSelected(selectedOptions)

      if (singleSelect) {
        submitSelection(selectedOptions)
      }
    },
    [setSelected, singleSelect, getSortAttribute, submitSelection, onOptionsAdded, selected, filterValue],
  )

  // Convert from Options to Items

  // Converted items are cached per option object so a re-render doesn't rebuild
  // them. `convertOptionToItem` derives group membership from `selected`, so the
  // cache must be discarded when the selection changes — otherwise an item keeps
  // the `groupId` it was first converted with and never moves between groups.
  const optionMap = useMemo(() => {
    void convertOptionToItem // silence unused warning
    void selected // cache is keyed on identity; selection changes invalidate it
    return new WeakMap<T, ItemProps>()
  }, [convertOptionToItem, selected])
  const selectedOptionIdsSet = useMemo(() => new Set(selected.map(option => option.id)), [selected])

  const getItemFromOption = useCallback(
    (option: T) => {
      let item = optionMap.get(option)
      if (!item) {
        item = convertOptionToItem(option, selected)
        // eslint-disable-next-line react-hooks/immutability
        optionMap.set(option, item)
      }
      return item
    },
    [optionMap, convertOptionToItem, selected],
  )

  const filteredItems = useMemo<Array<ItemProps>>(() => {
    return (filteredOptions ?? []).map(getItemFromOption)
  }, [filteredOptions, getItemFromOption])

  const isAssigneeLimitReached = useAssigneeLimitReached(columnId, selected.length, maximumAssignees)

  const itemsWithDisabledState = useMemo<Array<ItemProps>>(() => {
    if (!isAssigneeLimitReached) {
      return filteredItems
    }

    return filteredItems.map(item => {
      const isSelected = item.id !== undefined && selectedOptionIdsSet.has(item.id)
      if (isSelected) {
        return item
      }
      return {
        ...item,
        disabled: true,
      }
    })
  }, [filteredItems, isAssigneeLimitReached, selectedOptionIdsSet])

  const selectedItems = useMemo<ItemProps | Array<ItemProps>>(() => {
    if (singleSelect) {
      // the selected item list needs to be aware of whether filtering is
      // occurring to ensure it locates the right item, as the item selected may
      // not be visible by allOptions on initial render
      const availableOptions = filteredOptions ? filteredOptions : allOptions || []
      const items = availableOptions.filter(option => selectedOptionIdsSet.has(option.id)).map(getItemFromOption)
      return not_typesafe_nonNullAssertion(items[0])
    } else {
      // for multi-select cases we assume allOptions will have everything we
      // need, so we can disregard the filtered options
      const availableOptions = allOptions ?? []
      return availableOptions.filter(option => selectedOptionIdsSet.has(option.id)).map(getItemFromOption)
    }
  }, [filteredOptions, allOptions, getItemFromOption, singleSelect, selectedOptionIdsSet]) as Array<ItemProps>

  const shouldShowCreateOption =
    filterValue &&
    filteredOptions &&
    getOptionMatchingFilterValue &&
    !filteredOptions.some(option => getOptionMatchingFilterValue(filterValue, option))

  const items = [
    ...itemsWithDisabledState.map(item => {
      if (singleSelect) {
        if (item.children) {
          item.children = <span className={styles.Text}>{item.children}</span>
        }
        item.onAction = (itemFromAction, event) => {
          event.preventDefault()
          // This is a bit awkward because selectedItem can be ItemProps or ItemProps[].
          // For single selection, we want to cast selectedItem as ItemProps singular.
          // To do this we first need to cast it to unknown first, before ItemProps otherwise
          // we get a type error complaining selectedItem: ItemProps[] does not have id required in ItemProps
          const selectedItem = selectedItems as unknown as ItemProps
          onSelectedChange(selectedItem === item ? undefined : item)
          onOpenChange(false, 'selection', event)
        }
      }

      return item
    }),
    ...(shouldShowCreateOption && renderCreateNewOption ? [renderCreateNewOption(filterValue)] : []),
  ]

  const renderErrorItem = useCallback(() => {
    if (!fetchSuggestionsError) return <></>
    return <SelectMenuError error={fetchSuggestionsError} />
  }, [fetchSuggestionsError])

  const props: SelectPanelProps = {
    open: open ?? true,
    showItemDividers: true,
    onSelectedChange,
    selected: selectedItems,
    filterValue,
    onFilterChange: setFilterValue,
    onOpenChange,
    items,
    loading: !filteredOptions && !fetchSuggestionsError,
    placeholderText,
    renderAnchor,
  }

  if (groupMetadata) {
    props.groupMetadata = groupMetadata.filter(group => {
      return items.some(item => 'groupId' in item && item.groupId === group.groupId)
    })
  }

  if (fetchSuggestionsError) {
    props.renderItem = renderErrorItem
    props.textInputProps = {disabled: true}
    props.items = [{id: 'error-item'}] // we need at least one item to render the error
    return {
      ...props,
      ...testIdProps(`table-cell-editor-error`),
    }
  }

  // Set message when items are empty
  if (items.length === 0) {
    if (filterValue) {
      // Filtering returned no results
      props.message = {...DEFAULT_BLANKSLATE_MESSAGE, title: 'No matches'}
    } else if (blankslateText) {
      // Truly empty (no filter, no items)
      props.message = {...DEFAULT_BLANKSLATE_MESSAGE, title: blankslateText}
    }
  }

  return props
}
